import { SubscriptionPlan } from "../types/payment";

export interface StorageLimits {
  videoStorage: number;
  fileStorage: number;
  maxVideoSize: number;
  maxFileSize: number;
}

export interface PlanFeatures {
  maxCourses: number | null;
  maxLessonsPerCourse: number | null;
  maxStudents: number | null;
  maxInstructors: number;
  storageGB: StorageLimits;
  highSpeedUpload: boolean;
  noRevenueCut: boolean;
  customDomain: boolean;
  whiteLabel: boolean;
  teamManagement: boolean;
  prioritySupport: boolean;
  sla: boolean;
  allowAddons: boolean;
  hasDashboard: boolean;
}

export interface StorageAddon {
  id: string;
  name: string;
  storageGB: number;
  maxStudents: number;
  price: number;
  description: string;
}

// Add-ons for Pro/Pro+ - ซื้อครั้งเดียว ใช้ได้ตลอด (เพิ่มพื้นที่เท่านั้น)
export const PRO_ADDONS: StorageAddon[] = [
  {
    id: "addon_5gb",
    name: "+5GB",
    storageGB: 5,
    maxStudents: 0,
    price: 99,
    description: "เพิ่มพื้นที่จัดเก็บ 5GB (ซื้อครั้งเดียว ใช้ตลอด)",
  },
  {
    id: "addon_10gb",
    name: "+10GB",
    storageGB: 10,
    maxStudents: 0,
    price: 199,
    description: "เพิ่มพื้นที่จัดเก็บ 10GB (ซื้อครั้งเดียว ใช้ตลอด)",
  },
  {
    id: "addon_25gb",
    name: "+25GB",
    storageGB: 25,
    maxStudents: 0,
    price: 490,
    description: "เพิ่มพื้นที่จัดเก็บ 25GB (ซื้อครั้งเดียว ใช้ตลอด)",
  },
  {
    id: "addon_50gb",
    name: "+50GB",
    storageGB: 50,
    maxStudents: 0,
    price: 990,
    description: "เพิ่มพื้นที่จัดเก็บ 50GB (ซื้อครั้งเดียว ใช้ตลอด)",
  },
  {
    id: "addon_100gb",
    name: "+100GB",
    storageGB: 100,
    maxStudents: 0,
    price: 1790,
    description: "เพิ่มพื้นที่จัดเก็บ 100GB (ซื้อครั้งเดียว ใช้ตลอด)",
  },
  {
    id: "addon_200gb",
    name: "+200GB",
    storageGB: 200,
    maxStudents: 0,
    price: 2990,
    description: "เพิ่มพื้นที่จัดเก็บ 200GB (ซื้อครั้งเดียว ใช้ตลอด)",
  },
];

// Legacy - kept for backward compatibility
export const PRO_MONTHLY_ADDONS: StorageAddon[] = PRO_ADDONS;
export const YEARLY_ADDONS: StorageAddon[] = [];

// Legacy - kept for backward compatibility, will be removed
export const STORAGE_ADDONS: StorageAddon[] = PRO_ADDONS;

const GB = 1024;

// 5-TIER SYSTEM - Free Trial + Pro (Monthly/Yearly) + Pro+ (Monthly/Yearly)
export const SUBSCRIPTION_PLANS: SubscriptionPlan[] = [
  // 1) Free Trial - ทดลองใช้ฟรี 14 วัน (สำหรับทดสอบระบบเท่านั้น)
  {
    tier: "free",
    name: "Free Trial (14 วัน)",
    price: 0,
    period: "ฟรี 14 วัน",
    features: {
      maxCourses: 1,
      maxLessonsPerCourse: null,
      maxStudents: 20,
      maxInstructors: 1,
      storageGB: {
        videoStorage: 5 * GB,
        fileStorage: 5 * GB,
        maxVideoSize: 2 * GB,
        maxFileSize: 100,
      },
      highSpeedUpload: false,
      noRevenueCut: false,
      customDomain: false,
      whiteLabel: false,
      teamManagement: false,
      prioritySupport: false,
      sla: false,
      allowAddons: false,
      hasDashboard: false,
    },
  },
  // 2) Pro Monthly - 690 บาท/เดือน (20 GB)
  {
    tier: "pro_monthly",
    name: "Pro (รายเดือน)",
    price: 690,
    period: "/ เดือน",
    features: {
      maxCourses: null,
      maxLessonsPerCourse: null,
      maxStudents: null,
      maxInstructors: 1,
      storageGB: {
        videoStorage: 20 * GB,
        fileStorage: 20 * GB,
        maxVideoSize: 500,
        maxFileSize: 50,
      },
      highSpeedUpload: true,
      noRevenueCut: true,
      customDomain: false,
      whiteLabel: false,
      teamManagement: false,
      prioritySupport: false,
      sla: false,
      allowAddons: true,
      hasDashboard: true,
    },
  },
  // 3) Pro Semi-Annual - 3,795 บาท/6 เดือน (22 GB = 20 GB + 2 GB bonus)
  {
    tier: "pro_semi_annual",
    name: "Pro (ราย 6 เดือน)",
    price: 3795,
    period: "/ 6 เดือน",
    features: {
      maxCourses: null,
      maxLessonsPerCourse: null,
      maxStudents: null,
      maxInstructors: 1,
      storageGB: {
        videoStorage: 22 * GB,
        fileStorage: 22 * GB,
        maxVideoSize: 500,
        maxFileSize: 50,
      },
      highSpeedUpload: true,
      noRevenueCut: true,
      customDomain: false,
      whiteLabel: false,
      teamManagement: false,
      prioritySupport: false,
      sla: false,
      allowAddons: true,
      hasDashboard: true,
    },
  },
  // 4) Pro Yearly - 6,900 บาท/ปี (25 GB = 20 GB + 5 GB bonus)
  {
    tier: "pro_yearly",
    name: "Pro (รายปี)",
    price: 6900,
    period: "/ ปี",
    features: {
      maxCourses: null,
      maxLessonsPerCourse: null,
      maxStudents: null,
      maxInstructors: 1,
      storageGB: {
        videoStorage: 25 * GB,
        fileStorage: 25 * GB,
        maxVideoSize: 500,
        maxFileSize: 50,
      },
      highSpeedUpload: true,
      noRevenueCut: true,
      customDomain: false,
      whiteLabel: false,
      teamManagement: false,
      prioritySupport: false,
      sla: false,
      allowAddons: true,
      hasDashboard: true,
    },
  },
  // 5) Pro+ Monthly - 1,590 บาท/เดือน (30 GB)
  {
    tier: "pro_plus_monthly",
    name: "Pro+ (รายเดือน)",
    price: 1590,
    period: "/ เดือน",
    features: {
      maxCourses: null,
      maxLessonsPerCourse: null,
      maxStudents: null,
      maxInstructors: 1,
      storageGB: {
        videoStorage: 30 * GB,
        fileStorage: 30 * GB,
        maxVideoSize: 500,
        maxFileSize: 50,
      },
      highSpeedUpload: true,
      noRevenueCut: true,
      customDomain: false,
      whiteLabel: false,
      teamManagement: false,
      prioritySupport: false,
      sla: false,
      allowAddons: true,
      hasDashboard: true,
      salePageBuilder: true,
      seoOptimization: true,
    },
  },
  // 6) Pro+ Semi-Annual - 8,745 บาท/6 เดือน (32 GB = 30 GB + 2 GB bonus)
  {
    tier: "pro_plus_semi_annual",
    name: "Pro+ (ราย 6 เดือน)",
    price: 8745,
    period: "/ 6 เดือน",
    features: {
      maxCourses: null,
      maxLessonsPerCourse: null,
      maxStudents: null,
      maxInstructors: 1,
      storageGB: {
        videoStorage: 32 * GB,
        fileStorage: 32 * GB,
        maxVideoSize: 500,
        maxFileSize: 50,
      },
      highSpeedUpload: true,
      noRevenueCut: true,
      customDomain: false,
      whiteLabel: false,
      teamManagement: false,
      prioritySupport: false,
      sla: false,
      allowAddons: true,
      hasDashboard: true,
      salePageBuilder: true,
      seoOptimization: true,
    },
  },
  // 7) Pro+ Yearly - 15,900 บาท/ปี (35 GB = 30 GB + 5 GB bonus)
  {
    tier: "pro_plus_yearly",
    name: "Pro+ (รายปี)",
    price: 15900,
    period: "/ ปี",
    features: {
      maxCourses: null,
      maxLessonsPerCourse: null,
      maxStudents: null,
      maxInstructors: 1,
      storageGB: {
        videoStorage: 35 * GB,
        fileStorage: 35 * GB,
        maxVideoSize: 500,
        maxFileSize: 50,
      },
      highSpeedUpload: true,
      noRevenueCut: true,
      customDomain: false,
      whiteLabel: false,
      teamManagement: false,
      prioritySupport: false,
      sla: false,
      allowAddons: true,
      hasDashboard: true,
      salePageBuilder: true,
      seoOptimization: true,
    },
  },
];

export const getPlanByTier = (tier: string): SubscriptionPlan | undefined => {
  // Handle legacy tiers for backward compatibility
  let normalizedTier = tier;
  if (tier === "yearly") normalizedTier = "pro_yearly"; // Legacy yearly -> pro_yearly
  if (tier === "pro") normalizedTier = "pro_monthly"; // Legacy pro -> pro_monthly
  if (tier === "pro_plus") normalizedTier = "pro_plus_monthly"; // Legacy pro_plus -> pro_plus_monthly
  
  return SUBSCRIPTION_PLANS.find((plan) => plan.tier === normalizedTier);
};

export const canAccessFeature = (
  userTier: string,
  feature: keyof SubscriptionPlan["features"]
): boolean => {
  const plan = getPlanByTier(userTier);
  return plan ? plan.features[feature] === true : false;
};

export const canCreateCourse = (userTier: string, currentCourseCount: number): boolean => {
  const plan = getPlanByTier(userTier);
  if (!plan) return false;
  if (plan.features.maxCourses === null) return true;
  return currentCourseCount < plan.features.maxCourses;
};

export const canAddLesson = (userTier: string, currentLessonCount: number): boolean => {
  const plan = getPlanByTier(userTier);
  if (!plan) return false;
  if (plan.features.maxLessonsPerCourse === null) return true;
  return currentLessonCount < plan.features.maxLessonsPerCourse;
};

export const canAddStudent = (userTier: string, currentStudentCount: number): boolean => {
  const plan = getPlanByTier(userTier);
  if (!plan) return false;
  if (plan.features.maxStudents === null) return true;
  return currentStudentCount < plan.features.maxStudents;
};

export const canAddInstructor = (userTier: string, currentInstructorCount: number): boolean => {
  const plan = getPlanByTier(userTier);
  if (!plan) return false;
  if (plan.features.maxInstructors === null) return true;
  return currentInstructorCount < plan.features.maxInstructors;
};

export const canUploadFile = (
  userTier: string,
  fileSize: number,
  isVideo: boolean,
  currentVideoUsage: number,
  currentFileUsage: number,
  addonStorageGB: number = 0
): { canUpload: boolean; reason?: string } => {
  const plan = getPlanByTier(userTier);
  if (!plan) return { canUpload: false, reason: "Invalid plan" };

  const fileSizeMB = fileSize / (1024 * 1024);
  const maxSize = isVideo
    ? plan.features.storageGB.maxVideoSize
    : plan.features.storageGB.maxFileSize;

  if (fileSizeMB > maxSize) {
    return {
      canUpload: false,
      reason: `ไฟล์มีขนาดใหญ่เกินกว่าที่แพ็กเกจรองรับ (สูงสุด ${maxSize >= 1024 ? (maxSize / 1024).toFixed(0) + ' GB' : maxSize + ' MB'})`,
    };
  }

  const totalStorage = isVideo
    ? plan.features.storageGB.videoStorage + addonStorageGB * 1024
    : plan.features.storageGB.fileStorage + addonStorageGB * 1024;

  const currentUsage = isVideo ? currentVideoUsage : currentFileUsage;

  if (currentUsage + fileSizeMB > totalStorage) {
    return {
      canUpload: false,
      reason: "พื้นที่จัดเก็บเต็ม กรุณาอัปเกรดแพ็กเกจหรือเพิ่มพื้นที่",
    };
  }

  return { canUpload: true };
};

export const getStorageUsagePercentage = (
  userTier: string,
  currentUsage: number,
  isVideo: boolean,
  addonStorageGB: number = 0
): number => {
  const plan = getPlanByTier(userTier);
  if (!plan) return 0;

  const totalStorage = isVideo
    ? plan.features.storageGB.videoStorage + addonStorageGB * 1024
    : plan.features.storageGB.fileStorage + addonStorageGB * 1024;

  return (currentUsage / totalStorage) * 100;
};

export const shouldShowStorageWarning = (
  userTier: string,
  currentUsage: number,
  isVideo: boolean,
  addonStorageGB: number = 0
): boolean => {
  const percentage = getStorageUsagePercentage(userTier, currentUsage, isVideo, addonStorageGB);
  return percentage >= 80;
};

export const getUpgradeRecommendation = (
  currentTier: string
): { nextTier: string; message: string } | null => {
  const tierOrder = ["free", "pro_monthly", "pro_yearly", "pro_plus_monthly", "pro_plus_yearly"];
  const currentIndex = tierOrder.indexOf(currentTier);

  if (currentIndex === -1 || currentIndex === tierOrder.length - 1) {
    return null;
  }

  const nextTier = tierOrder[currentIndex + 1];
  const nextPlan = getPlanByTier(nextTier);

  if (!nextPlan) return null;

  const messages: Record<string, string> = {
    pro_monthly: "อัปเกรดเป็น Pro (รายเดือน) เพื่อสร้างคอร์สไม่จำกัด รองรับนักเรียนไม่จำกัด และใช้ Dashboard",
    pro_yearly: "อัปเกรดเป็น Pro (รายปี) เพื่อรับพื้นที่ 20GB และประหยัดกว่า",
    pro_plus_monthly: "อัปเกรดเป็น Pro+ (รายเดือน) เพื่อรับพื้นที่ 30GB และ SEO Optimization",
    pro_plus_yearly: "อัปเกรดเป็น Pro+ (รายปี) เพื่อรับพื้นที่ 30GB และประหยัดกว่า",
  };

  return {
    nextTier,
    message: messages[nextTier] || `อัปเกรดเป็น ${nextPlan.name} เพื่อขีดความสามารถที่มากขึ้น`,
  };
};

// Get add-ons based on user's current tier
export const getAddonsForTier = (tier: string): StorageAddon[] => {
  // All paid plans support add-ons
  if (tier !== "free") {
    return PRO_ADDONS;
  }
  return []; // Free Trial ไม่มี add-ons
};

export const getAddonRecommendation = (
  userTier: string,
  currentVideoUsage: number,
  currentFileUsage: number
): StorageAddon | null => {
  const plan = getPlanByTier(userTier);
  if (!plan || !plan.features.allowAddons) return null;

  const addons = getAddonsForTier(userTier);
  if (addons.length === 0) return null;

  const videoPercentage = (currentVideoUsage / plan.features.storageGB.videoStorage) * 100;
  const filePercentage = (currentFileUsage / plan.features.storageGB.fileStorage) * 100;

  if (videoPercentage >= 80 || filePercentage >= 80) {
    // แนะนำ add-on ตัวแรกของแพ็กเกจนั้น ๆ
    return addons[0];
  }

  return null;
};
